import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { StarBall } from '#src/atoms/icons/StarBall/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, DisplayS } from '#src/atoms/typography/index.tsx';
import { ConfirmDialog } from '#src/components/form/ConfirmDialog/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';

import type {
  UnlockProModeMutation,
  UnlockProModeMutationVariables,
} from './__generated__/index.graphql.ts';
import backgroundImage from './assets/background.webp';

const UNLOCK_PRO_MODE_MUTATION = gql`
  mutation UnlockProModeMutation($input: unlockProModeInput!, $sport: Sport!) {
    unlockProMode(input: $input) {
      currentUser {
        slug
        anyUserSportProfile(sport: $sport) {
          id
          proUnlocked
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<UnlockProModeMutation, UnlockProModeMutationVariables>;

const Root = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  width: 100%;
  min-height: 120px;
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  outline: 1px solid var(--c-nd-150);
  overflow: hidden;
  background: var(--background-url) top / cover no-repeat;
  cursor: pointer;
  transition:
    transform 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(var(--c-rgb-black), 0.4),
      rgba(var(--c-rgb-black), 0.6)
    );
    z-index: 1;
  }

  &:hover {
    &::before {
      background: linear-gradient(
        to bottom,
        rgba(var(--c-rgb-black), 0.2),
        rgba(var(--c-rgb-black), 0.3)
      );
      z-index: 1;
    }
    box-shadow: 0 4px 12px rgba(var(--c-rgb-black), 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Content = styled(Horizontal)`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
`;

const messages = defineMessages({
  goPro: {
    id: 'GoProBlock.goPro',
    defaultMessage: 'Go Pro',
  },
  confirmationMessage: {
    id: 'GoProBlock.confirmationMessage',
    defaultMessage: 'Are you sure you want to unlock Pro mode?',
  },
});

export const GoProBlock = ({ onClose }: { onClose: () => void }) => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const [unlockProMode, { loading }] = useMutation(UNLOCK_PRO_MODE_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  if (currentUser?.anyUserSportProfile?.proUnlocked) {
    return null;
  }
  const handleConfirm = async () => {
    await unlockProMode({
      variables: {
        sport,
        input: {
          sport,
          interactionContext: 'user-nav',
        },
      },
    });
    setShowConfirmDialog(false);
    onClose();
  };

  return (
    <>
      <Root
        type="button"
        onClick={() => setShowConfirmDialog(true)}
        disabled={loading}
        style={
          {
            '--background-url': `url(${backgroundImage})`,
          } as CSSProperties
        }
      >
        <Content center>
          <DisplayS color="var(--c-white)">
            <FormattedMessage {...messages.goPro} />
          </DisplayS>
          <StarBall width={12} height={12} />
        </Content>
      </Root>
      <ConfirmDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirm}
        title={<FormattedMessage {...messages.goPro} />}
        message={
          <BodyM className="text-center">
            <FormattedMessage {...messages.confirmationMessage} />
          </BodyM>
        }
      />
    </>
  );
};
